import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box, Button, Collapse, FormControlLabel, Grid, Typography, useTheme } from '@mui/material';
import { ISpeciesTable } from 'common/defines/analytic';
import { convertAnalyticNameToMultiLanguage } from 'common/utils/convert';
import ButtonSubmit from 'components/ButtonSubmit';
import { SwitchCustom } from 'components/Common/SwitchCustom';
import { useLayerSelectCardStyle } from 'components/MapView/MapViewStyle';
import { useCheckRTL } from 'hooks/common/useCheckRLF';
import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import { changeColorSpeciesCount } from 'services/analytics/apiAnalyticsConfig.services';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import {
  changeLayer3D,
  changeSpeciesCountDisplay,
  changeSpeciesCountEachLayer,
  mapViewSelector,
} from 'store/slices/mapViewSlice';
import { TYPE_ANALYTICS_MAP_VIEW } from '../../../../../../common/defines/constants';
import { QUERY_KEY } from '../../../../../../constants/constants';
import { PolygonLayerTypeRange } from '../PolygonLayerTypeRange';
import SideSurface from '../SideSurface';
import TopSurface from '../TopSurface';

interface SpeciesCountCardLayerProps {
  labelCard: string;
  layerList: ISpeciesTable[];
  setCurrentPage: (arg: number) => void;
}

export const SpeciesCountCardLayer: FC<SpeciesCountCardLayerProps> = ({ labelCard, layerList, setCurrentPage }) => {
  const [isOpenCollapse, setIsOpenCollapse] = useState<boolean>(true);
  const [isChangeColor, setIsChangeColor] = useState<boolean>(false);
  const [isShowAllLayer, setIsShowAllLayer] = useState<boolean>(true);
  const [changedColorInfo, setChangedColorInfo] = useState<{ key: number; color: string }>({ key: 0, color: '' });

  const { isLayer3D, analyticId, speciesCountDisplay } = useAppSelector(mapViewSelector);

  const { isRTL } = useCheckRTL();
  const theme = useTheme();
  const { t } = useTranslation();
  const classes = useLayerSelectCardStyle();
  const queryClient = useQueryClient();
  const dispatch = useAppDispatch();

  useEffect(() => {
    const initialDisplay = layerList.map((item) => ({
      key: item.speciesId,
      name: item.name,
      color: item.settingColor ?? item.defaultColor,
      visible: true,
    }));
    dispatch(changeSpeciesCountDisplay(initialDisplay));
  }, [dispatch, layerList]);

  const saveChangeColorMutation = useMutation(changeColorSpeciesCount, {
    onSuccess() {
      queryClient.invalidateQueries([QUERY_KEY.GET_All_SPECIES_COUNT]);
      toast.success('Change Color Successful');
    },
  });

  const handleChangeColors = (key: number, color: string) => {
    setChangedColorInfo({ key, color });
    const newSpeciesCountDisplay = speciesCountDisplay.map((item) => ({
      ...item,
      color: key === item.key ? color : item.color,
    }));
    dispatch(changeSpeciesCountDisplay(newSpeciesCountDisplay));
    setIsChangeColor(true);
  };

  const handleSelectAll = (e: any) => {
    const isChecked = e.target.checked;
    setIsShowAllLayer((prev) => !prev);
    const newSpeciesCountDisplay = speciesCountDisplay.map((item) => ({ ...item, visible: isChecked }));
    dispatch(changeSpeciesCountDisplay(newSpeciesCountDisplay));
  };

  const handleSaveChangeColor = () => {
    setIsChangeColor(false);
    const { color, key } = changedColorInfo;
    saveChangeColorMutation.mutate({ analyticId: analyticId!, speciesId: key, settingColor: color });
    setCurrentPage(1);
  };

  return (
    <>
      {speciesCountDisplay?.length > 0 && (
        <Box sx={{ p: '8px' }}>
          <Box>
            <Button
              startIcon={isOpenCollapse ? <ExpandMoreIcon /> : <ChevronRightIcon />}
              onClick={() => setIsOpenCollapse(!isOpenCollapse)}
              classes={{ root: classes.buttonTextStyle }}>
              {t(convertAnalyticNameToMultiLanguage(labelCard))}
            </Button>
            <Collapse in={isOpenCollapse} timeout="auto" unmountOnExit sx={{ px: 2.5 }}>
              <Box>
                <FormControlLabel
                  labelPlacement={isRTL ? 'start' : 'end'}
                  control={<SwitchCustom checked={isLayer3D} onClick={() => dispatch(changeLayer3D(!isLayer3D))} />}
                  label={<Typography className={classes.selectText}>{isLayer3D ? '3D' : '2D'}</Typography>}
                />
              </Box>
              <FormControlLabel
                labelPlacement={isRTL ? 'start' : 'end'}
                control={<SwitchCustom checked={isShowAllLayer} onClick={handleSelectAll} />}
                label={
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                    }}>
                    <Typography className={classes.selectText}>
                      {isShowAllLayer ? t('trans.unselect_all') : t('trans.select_all')}
                    </Typography>
                  </Box>
                }
              />

              {speciesCountDisplay?.length &&
                speciesCountDisplay.map((item) => {
                  return (
                    <PolygonLayerTypeRange
                      key={item.key}
                      label={item.name}
                      color={item.color}
                      id={item.key}
                      index={item.key}
                      visible={item.visible}
                      changeColorAnalyticsLayer={handleChangeColors}
                      changeVisibleAnalyticsLayer={changeSpeciesCountEachLayer}
                      typeOfAnalytic={TYPE_ANALYTICS_MAP_VIEW.SPECIES_COUNT}
                    />
                  );
                })}

              {isChangeColor && (
                <Grid container top={2} paddingTop={2} justifyContent={'center'} gap={2}>
                  <Grid item xs={4}>
                    <Button
                      color="neutral"
                      variant="outlined"
                      sx={{
                        width: '100%',
                        height: '40px',
                        borderRadius: '5px',
                        p: '12px 10px',
                        mb: '30px',
                        textTransform: 'none',
                        border: `1px solid ${theme.palette.divider}`,
                        color: theme.palette.mode === 'dark' ? '#fff' : '#000',
                      }}
                      onClick={() => {
                        setIsChangeColor(false);
                        queryClient.invalidateQueries([QUERY_KEY.GET_All_SPECIES_COUNT]);
                      }}>
                      Cancel
                    </Button>
                  </Grid>
                  <Grid item xs={4}>
                    <ButtonSubmit
                      onClick={() => {
                        setIsChangeColor(false);
                        handleSaveChangeColor();
                      }}>
                      Save
                    </ButtonSubmit>
                  </Grid>
                </Grid>
              )}
            </Collapse>
          </Box>

          <Grid item xs={12} md={12}>
            {isLayer3D && <SideSurface />}
          </Grid>

          <TopSurface />
        </Box>
      )}
    </>
  );
};
